import React, { useEffect, useState, useContext, useMemo, useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { ModuleBase, HeadingTag, Link, EventCard } from '@/components';
import ReportListCard from '@/components/report/ReportListCard';
import { searchReports } from '@/api/report-data';
import { UserContext } from '@/components';
import { ModuleBaseModel, ModuleBasePropsModel } from '@/types';
import { v4 as uuidv4 } from 'uuid';

interface ColumnConfig {
  id?: string;
  heading: string;
  cardType?: string;
  requestFilter?: any;
  link?: {
    href: string;
  };
  cards?: any[];
}

// Combined module interface following the pattern
export type ThreeColumnListingModel = ModuleBaseModel<
  {
    title?: string;
    columns?: ColumnConfig[];
  } & ModuleBasePropsModel
>;

const ThreeColumnListing = (data: ThreeColumnListingModel) => {
  const { props } = data;
  const t = useTranslations();
  const user = useContext(UserContext);
  const [moduleData, setModuleData] = useState<{ columns: ColumnConfig[] } | null>(null);
  const [loading, setLoading] = useState(false);

  // Memoize initial processed data
  const initialProcessedData = useMemo(() => {
    if (!props?.columns) return null;
    return {
      columns: props.columns.map((column) => ({
        id: column.id || uuidv4(),
        ...column,
        cards: column.cards || [],
      })),
    };
  }, [props?.columns]);

  // Optimized data fetching with parallel API calls
  const fetchColumnData = useCallback(
    async (processedData: { columns: ColumnConfig[] }) => {
      setLoading(true);

      const apiCalls = processedData.columns
        .filter((column) => column.cardType && column.cardType !== 'EventCardModel' && column.requestFilter)
        .map(async (column) => {
          try {
            const result = await searchReports({
              ...column.requestFilter,
              account: user?.account,
              userId: user?.userId,
            });
            return { columnId: column.id, cards: result.results || [] };
          } catch (error) {
            console.error('Error fetching reports for column:', column.id, error);
            return { columnId: column.id, cards: [] };
          }
        });

      const results = await Promise.all(apiCalls);

      const updatedColumns = processedData.columns.map((column) => {
        const result = results.find((r) => r.columnId === column.id);
        return result ? { ...column, cards: result.cards } : column;
      });

      setModuleData({ columns: updatedColumns });
      setLoading(false);
    },
    [user?.account, user?.userId],
  );

  useEffect(() => {
    if (!initialProcessedData) return;

    setModuleData(initialProcessedData);

    const hasApiColumns = initialProcessedData.columns.some(
      (column) => column.cardType && column.cardType !== 'EventCardModel' && column.requestFilter,
    );

    if (hasApiColumns) {
      fetchColumnData(initialProcessedData);
    }
  }, [initialProcessedData, fetchColumnData]);

  if (!moduleData) {
    return (
      <ModuleBase data={data}>
        <div className="container mx-auto px-4">
          <div className="animate-pulse">
            <div className="mb-8 h-8 w-1/3 rounded bg-gray-300"></div>
            <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="space-y-4">
                  <div className="h-6 w-1/2 rounded bg-gray-300"></div>
                  <div className="space-y-3">
                    {[1, 2, 3].map((j) => (
                      <div key={j} className="h-20 rounded bg-gray-300"></div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </ModuleBase>
    );
  }

  return (
    <ModuleBase data={data}>
      <div className="container mx-auto px-4">
        {props.title && (
          <div className="flex items-center pb-8 md:pb-10 print:hidden">
            <div className="w-7/12 pb-3 sm:w-8/12 sm:pb-5">
              <HeadingTag className="text-2xl font-bold lg:text-4xl" tag="h2" heading={props.title} />
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 gap-0 lg:grid-cols-3 lg:gap-4">
          {moduleData.columns.map((column, index) => (
            <div
              key={column.id}
              className={`px-0 pt-2 md:px-4 ${
                index !== 0 ? 'border-t border-gray-300 pt-10 md:border-t-0 md:border-l md:pt-2' : ''
              }`}
            >
              <div className="flex flex-col">
                <div className="mb-4 flex items-start justify-between">
                  <div className="w-7/12 text-lg font-semibold md:w-8/12">
                    <div className="flex min-h-[60px] items-center">{column.heading}</div>
                  </div>
                  {column.link && (
                    <div className="w-5/12 text-right md:w-4/12" style={{ paddingTop: '14px' }}>
                      <Link link={column.link} className="btn underline">
                        {t('general.$viewAll')}
                      </Link>
                    </div>
                  )}
                </div>

                <div className="">
                  {loading && column.requestFilter ? (
                    <div className="animate-pulse space-y-3">
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="h-16 rounded bg-gray-200"></div>
                      ))}
                    </div>
                  ) : (
                    column.cards?.map((card, cardIndex) => (
                      <div key={cardIndex}>
                        {column.cardType === 'EventCardModel' ? (
                          <EventCard eventCard={card} />
                        ) : (
                          <ReportListCard data={card} small />
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </ModuleBase>
  );
};

export default ThreeColumnListing;
